// ── BatchReceiptUpload.js — 영수증 일괄 업로드 + 항목별 카테고리 편집 ──────
// 한 번에 여러 장 → 동시 OCR 5개씩 → 각 영수증 카드 → "항목 편집" 버튼 →
// 영수증의 모든 lineItem이 카테고리 드롭다운과 함께 펼쳐짐 → 카테고리별로
// 자동 그룹핑되어 거래 N건으로 저장.
//
// UX 핵심: + 항목 추가 안 함. AI가 추출한 lineItems를 모두 보여주고 사용자는
// 카테고리 드롭다운만 클릭. 같은 카테고리 항목은 자동 합산.

import React, { useState, useRef } from 'react';
import { useLang } from '../i18n/LangContext';   // V108 매장 통화·국가
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { usePlan } from '../usePlan';
import { useOcrQuota, consumeOcr, quotaMessage, OCR_QUOTA } from '../utils/ocrQuota';
import { fingerprintFromOcr, fingerprintFromTx, fingerprintFromPurchaseLog, buildFingerprintSet } from '../utils/receiptFingerprint';
import { db } from '../db';
import { ocrReceipt, ocrToTransaction, lineItemsToTransactions } from '../utils/ocr';
import { uploadReceiptImage } from '../utils/receiptStorage';
import { CATEGORY_MAJOR, CATEGORY_SUB, categoryLabel } from '../data/taxConstants';
import { L, M, MU, Lv } from '../i18n/L';   // V160 중국어

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('ko-KR');
const CONCURRENCY = 3;  // Anthropic Tier 1 rate limit 안전 마진
const CHUNK_DELAY_MS = 2000;  // 청크 간 sleep
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

function makeCard(file, idx) {
  return {
    id: `c_${idx}_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
    file,
    fileName: file.name,
    previewUrl: null,
    status: 'queued',
    progress: 0,
    error: null,
    ocr: null,
    lineItems: null,           // 사용자가 편집한 lineItems (카테고리 + included)
    selected: true,
    type: 'expense',
    mode: 'ledger',            // 'ledger' = 거래장부 입력 / 'memo' = 품목·자산화만 (간이영수증)
  };
}

// OCR lineItems를 편집 가능한 형태로 변환 (included=true 기본, category 채움)
function initLineItems(ocr) {
  if (!ocr || !Array.isArray(ocr.lineItems) || ocr.lineItems.length === 0) {
    return null;
  }
  return ocr.lineItems.map((li, i) => ({
    id: `li_${i}`,
    itemName: li.itemName || '',
    amount: Number(li.amount) || 0,
    quantity: Number(li.quantity) || (Number(li.amount) ? 1 : 0),  // 영수증 수량 (자산화용)
    unitPrice: Number(li.unitPrice) || 0,               // 영수증 단가
    discountAmount: Number(li.discountAmount) || 0,    // Case A 할인 표시용
    isDiscount: !!li.isDiscount,                        // Case B 전체 할인 라인
    isReturn: !!li.isReturn,                            // Case C 반품/교환 라인
    isTaxFree: !!li.isTaxFree,                          // 면세 여부 (부가세 신고 정확성)
    itemDate: li.itemDate || null,                      // 거래명세서 행별 거래일자
    category: li.suggestedCategory && li.suggestedCategory.major
      ? { major: li.suggestedCategory.major, sub: li.suggestedCategory.sub || null }
      : (ocr.category || { major: 'material', sub: 'raw_material' }),
    presumedCandidate: !!li.presumedCandidate,
    presumedReason: li.presumedReason || null,
    included: true,
  }));
}

export default function BatchReceiptUpload({
  user,
  defaultType = 'expense',
  onSaveAll,
  onClose,
  existingTransactions = [],
}) {
  const { country } = useLang();   // V108
  const [cards, setCards] = useState([]);
  const [running, setRunning] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [editorCard, setEditorCard] = useState(null);
  const cameraInputRef = useRef(null);    // 📷 카메라 (capture, 1장씩) — 모바일
  const [webcamOpen, setWebcamOpen] = useState(false);  // 💻 PC 웹캠 모달

  // V103: OCR 쿼터 체크 (총량 + 월 한도)
  const { trialActive } = usePlan(user);
  const quota = useOcrQuota(user, { trialActive });

  // 모바일이면 native capture 사용, PC면 웹캠 모달
  const isMobileDevice = typeof navigator !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent);
  const handleCameraClick = () => {
    if (isMobileDevice) cameraInputRef.current?.click();
    else setWebcamOpen(true);
  };
  const handleWebcamCapture = (file) => {
    setWebcamOpen(false);
    if (file) handleFiles([file]);
  };
  const galleryInputRef = useRef(null);   // 🖼 갤러리 (multiple)

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;
    if (files.length > 30) {
      alert(L("한 번에 최대 30장까지 가능합니다. 30장으로 제한합니다."));
      files.length = 30;
    }
    // V103: OCR 쿼터 체크 (총량 + 월 한도)
    if (files.length > quota.remaining) {
      alert(L("영수증 OCR 한도를 초과합니다.\n") +
        quotaMessage(quota, { trialActive }) + L("\n남은 장수: ") + quota.remaining + L("장\n\n") +
        (quota.remainingTotal <= quota.remainingMonth
          ? L("충전권(") + OCR_QUOTA.couponSize + L("장 ") + OCR_QUOTA.couponPrice.toLocaleString() + L("원)을 이용권 페이지에서 등록하면 계속 쓸 수 있어요.")
          : L("월 한도는 다음 달 1일에 초기화돼요.")));
      return;
    }

    const newCards = files.map((f, i) => {
      const c = makeCard(f, cards.length + i);
      c.type = defaultType;
      return c;
    });

    await Promise.all(newCards.map(c => new Promise(res => {
      const reader = new FileReader();
      reader.onload = () => { c.previewUrl = reader.result; res(); };
      reader.onerror = () => res();
      reader.readAsDataURL(c.file);
    })));

    setCards(prev => [...prev, ...newCards]);
    runOcrBatch(newCards);
  };

  const runOcrBatch = async (toRun) => {
    if (toRun.length === 0) return;
    setRunning(true);

    for (let i = 0; i < toRun.length; i += CONCURRENCY) {
      const chunk = toRun.slice(i, i + CONCURRENCY);
      await Promise.all(chunk.map(async (card) => {
        setCards(prev => prev.map(c => c.id === card.id ? { ...c, status: 'processing', progress: 30 } : c));
        try {
          const ocr = await ocrReceipt(card.file, {
            country,
            type: card.type,
            onRetry: ({ attempt, waitMs }) => {
              setCards(prev => prev.map(c => c.id === card.id
                ? { ...c, status: 'processing', progress: 20,
                    error: `⏳ AI 한도 도달 — ${Math.round(waitMs/1000)}초 후 자동 재시도 (${attempt}/2)` }
                : c));
            },
          });
          consumeOcr(user, 1); // V103: 사용량 기록 (성공 시)
          if (!ocr.confidence || ocr.confidence < 0.2) {
            setCards(prev => prev.map(c => c.id === card.id
              ? { ...c, status: 'error', progress: 100, error: '인식 실패 — 다시 찍어주세요' }
              : c));
            return;
          }
          setCards(prev => prev.map(c => c.id === card.id
            ? { ...c, status: 'done', progress: 100, ocr, error: null,
                lineItems: initLineItems(ocr),
                // 거래명세서 또는 간이영수증 → 매입메모 모드. 그 외 → 거래장부
                mode: (ocr.documentType === 'statement' || ocr.evidenceType === 'simple_receipt' || ocr.evidenceType === 'statement') ? 'memo' : 'ledger',
              }
            : c));
        } catch (e) {
          setCards(prev => prev.map(c => c.id === card.id
            ? { ...c, status: 'error', progress: 100, error: e.message || 'OCR 실패' }
            : c));
        }
      }));
      if (i + CONCURRENCY < toRun.length) {
        await sleep(CHUNK_DELAY_MS);
      }
    }

    setRunning(false);
  };

  const retryCard = (cardId) => {
    setCards(prev => prev.map(c => c.id === cardId
      ? { ...c, status: 'queued', progress: 0, error: null }
      : c));
    const card = cards.find(c => c.id === cardId);
    if (card) runOcrBatch([card]);
  };

  const removeCard = (cardId) => setCards(prev => prev.filter(c => c.id !== cardId));
  const toggleSelect = (cardId) => setCards(prev => prev.map(c => c.id === cardId ? { ...c, selected: !c.selected } : c));
  const setCardMode = (cardId, mode) => setCards(prev => prev.map(c => c.id === cardId ? { ...c, mode } : c));
  const setCardPartner = (cardId, partnerName) => setCards(prev => prev.map(c =>
    c.id === cardId ? { ...c, ocr: { ...c.ocr, partnerName } } : c
  ));

  // 항목 편집 모달에서 저장 시 호출
  const updateCardLineItems = (cardId, newLineItems) => {
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, lineItems: newLineItems } : c));
  };

  const saveAll = async () => {
    const toSave = cards.filter(c => c.selected && c.status === 'done' && c.ocr);
    if (toSave.length === 0) {
      alert(L("저장할 영수증이 없습니다."));
      return;
    }

    // ── 중복 영수증 사전 체크 (fingerprint = 거래일+거래처+총액) ──
    const existingTxFps = buildFingerprintSet(existingTransactions, fingerprintFromTx);
    const hasMemoCards = toSave.some(c => c.mode === 'memo');
    const existingLogFps = new Set();
    if (hasMemoCards && user?.uid) {
      try {
        const logSnap = await getDocs(collection(db, 'users', user.uid, 'purchaseLog'));
        logSnap.forEach(d => {
          const fp = fingerprintFromPurchaseLog(d.data());
          if (fp) existingLogFps.add(fp);
        });
      } catch (e) {
        console.warn('purchaseLog 조회 실패 (중복 체크 스킵):', e.message);
      }
    }
    const seenInBatch = new Set();
    const duplicateCards = [];
    const uniqueCards = [];
    for (const card of toSave) {
      const fp = fingerprintFromOcr(card.ocr);
      const dupExisting = card.mode === 'memo' ? existingLogFps.has(fp) : existingTxFps.has(fp);
      const dupBatch = fp && seenInBatch.has(fp);
      if (fp && (dupExisting || dupBatch)) {
        duplicateCards.push({ card, fp, reason: dupExisting ? '이전 저장' : '같은 배치 중복' });
      } else {
        uniqueCards.push({ card, fp });
        if (fp) seenInBatch.add(fp);
      }
    }
    if (duplicateCards.length > 0) {
      const lines = duplicateCards.slice(0, 5).map(d => {
        const o = d.card.ocr || {};
        const total = Number(o.total) || ((Number(o.supply) || 0) + (Number(o.vat) || 0));
        return L("  · {0} {1} {2} ({3})", o.date || '?', o.partnerName || '?', M(Math.round(total)), d.reason);
      }).join('\n');
      const more = duplicateCards.length > 5 ? L("\n  ... 외 {0}건", duplicateCards.length - 5) : '';
      const proceed = window.confirm(
        L("⚠️ 중복 영수증 {0}건 감지\n\n{1}{2}\n\n[확인] = 중복 건너뛰고 새 영수증만 저장 (권장)\n[취소] = 저장 중단", duplicateCards.length, lines, more)
      );
      if (!proceed) return;
    }
    if (uniqueCards.length === 0) {
      alert(L("저장할 새 영수증이 없습니다. (모두 중복)"));
      return;
    }
    const cardsToProcess = uniqueCards;

    setSavingAll(true);
    try {
      const allTxns = [];
      const cardLineItemsList = [];   // 가격 인텔리전스(itemPrices) 누적용
      const memoSaves = [];           // mode='memo' 카드들 — purchaseLog로 저장
      for (const { card, fp } of cardsToProcess) {
        // 영수증별 lineItems 원본도 함께 전달 — 매입(expense) 만 누적 (장부/메모 둘 다)
        if (card.type === 'expense' && card.lineItems && card.lineItems.length > 0) {
          cardLineItemsList.push({ card, lineItems: card.lineItems });
        }

        // ★ 영수증 원본 이미지를 Firebase Storage에 업로드 (세법 5년 보관)
        let receiptImageUrl = null;
        let receiptImagePath = null;
        if (user?.uid && card.ocr?._originalImage) {
          try {
            const tmpId = `${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
            const uploaded = await uploadReceiptImage(
              user.uid,
              card.ocr._originalImage,
              tmpId,
              card.ocr.date
            );
            receiptImageUrl = uploaded.url;
            receiptImagePath = uploaded.path;
          } catch (e) {
            console.warn('영수증 이미지 업로드 실패 (저장은 계속):', e.message);
          }
        }

        if (card.mode === 'memo') {
          memoSaves.push({ card, receiptImageUrl, receiptImagePath, fp });
          continue;
        }
        // 장부 모드: lineItems 있으면 카테고리별 그룹핑 변환, 없으면 단일 거래
        const txns = card.lineItems && card.lineItems.length > 0
          ? lineItemsToTransactions(card.ocr, card.lineItems, card.type)
          : [ocrToTransaction(card.ocr, card.type)];
        // 영수증 URL + fingerprint(중복방지용) 박기
        for (const tx of txns) {
          if (receiptImageUrl) {
            tx.receiptImageUrl = receiptImageUrl;
            tx.receiptImagePath = receiptImagePath;
          }
          if (fp) tx.receiptFingerprint = fp;
        }
        allTxns.push(...txns);
      }
      // 장부 거래 저장 (기존 흐름)
      if (allTxns.length > 0 || cardLineItemsList.length > 0) {
        await onSaveAll(allTxns, cardLineItemsList);
      }
      // 메모 거래 저장 (purchaseLog 컬렉션) — user 필수
      if (memoSaves.length > 0 && user?.uid) {
        const logRef = collection(db, 'users', user.uid, 'purchaseLog');
        for (const ms of memoSaves) {
          const card = ms.card;
          const items = (card.lineItems || []).filter(li => li.included !== false).map(li => ({
            itemName: li.itemName || '',
            amount: Number(li.amount) || 0,
            quantity: Number(li.quantity) || 1,
            unitPrice: Number(li.unitPrice) || 0,
            isTaxFree: !!li.isTaxFree,
            isDiscount: !!li.isDiscount,
            isReturn: !!li.isReturn,
            category: li.category || null,
          }));
          await addDoc(logRef, {
            date: card.ocr.date || null,
            partnerName: card.ocr.partnerName || '',
            partnerBizNumber: card.ocr.partnerBizNumber || null,
            evidenceType: card.ocr.evidenceType || 'simple_receipt',
            total: Number(card.ocr.total) || 0,
            supply: Number(card.ocr.supply) || 0,
            vat: Number(card.ocr.vat) || 0,
            taxFreeAmount: Number(card.ocr.taxFreeAmount) || 0,
            items,
            receiptImageUrl: ms.receiptImageUrl || null,
            receiptImagePath: ms.receiptImagePath || null,
            memo: '간이영수증 메모 (거래장부 미입력 — 정산 시 적격증빙으로 별도 입력 필요)',
            receiptFingerprint: ms.fp || null,
            createdAt: serverTimestamp(),
          });
        }
      } else if (memoSaves.length > 0 && !user?.uid) {
        alert(L("메모 모드 저장에 로그인 정보가 필요해요. 거래장부 모드로 저장하시거나 새로고침 후 다시 시도해주세요."));
      }
      onClose && onClose();
    } catch (e) {
      console.error(e);
      alert(L("저장 실패: {0}", e.message));
      setSavingAll(false);
    }
  };

  // 통계 — 저장 시 거래 몇 건 만들어질지 미리 계산
  const computeTxnCount = (card) => {
    if (!card.lineItems || card.lineItems.length === 0) return 1;
    const groups = new Set();
    for (const li of card.lineItems) {
      if (li.included === false) continue;
      groups.add(`${li.category?.major || 'etc'}::${li.category?.sub || ''}`);
    }
    return Math.max(1, groups.size);
  };

  const stats = {
    total: cards.length,
    done: cards.filter(c => c.status === 'done').length,
    error: cards.filter(c => c.status === 'error').length,
    selected: cards.filter(c => c.selected && c.status === 'done').length,
    txnsToCreate: cards.filter(c => c.selected && c.status === 'done').reduce((s, c) => s + computeTxnCount(c), 0),
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 };
  const modal = { background: '#fff', borderRadius: 14, width: 920, maxWidth: '100%', maxHeight: '94vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 50px rgba(0,0,0,0.25)' };
  const header = { padding: '14px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 14, background: '#f9fafb' };
  const body = { padding: 18, overflow: 'auto', flex: 1, background: '#fafafa' };
  const footer = { padding: '12px 20px', borderTop: '1px solid #eee', display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', background: '#fff' };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div style={modal}>
        <div style={header}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>{L("📸 영수증 일괄 업로드")}</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>{L("여러 장 한 번에 자동 입력")}</div>
          </div>
          {stats.total > 0 && (
            <div style={{ fontSize: 12, color: '#555', display: 'flex', gap: 12 }}>
              <span>{L("총")} <strong>{L("{0}장", stats.total)}</strong></span>
              <span style={{ color: '#18181B' }}>{L("완료 {0}", stats.done)}</span>
              {stats.error > 0 && <span style={{ color: '#52525B' }}>{L("실패 {0}", stats.error)}</span>}
              <span>{L("거래")} <strong>{L("{0}건", stats.txnsToCreate)}</strong> {L("예정")}</span>
            </div>
          )}
          <button onClick={onClose} type="button"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
        </div>

        <div style={body}>
          {webcamOpen && <WebcamModal onCapture={handleWebcamCapture} onClose={() => setWebcamOpen(false)} />}
          {cards.length === 0 ? (
            <UploadDropzone
              cameraInputRef={cameraInputRef}
              galleryInputRef={galleryInputRef}
              onCameraClick={handleCameraClick}
              onFiles={handleFiles} />
          ) : (
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, padding: '10px 14px', background: '#fff', borderRadius: 10, border: '1px solid #eee', flexWrap: 'wrap' }}>
                <button onClick={handleCameraClick} type="button"
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #FF6F0F', background: '#FF6F0F', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  {L("📷 카메라")}
                </button>
                <button onClick={() => galleryInputRef.current?.click()} type="button"
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #FF6F0F', background: '#fff', color: '#18181B', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  {L("🖼 갤러리")}
                </button>
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                  style={{ display: 'none' }}
                  onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
                <input ref={galleryInputRef} type="file" accept="image/*" multiple
                  style={{ display: 'none' }}
                  onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
                <div style={{ fontSize: 11, color: '#888', flex: 1, minWidth: 140 }}>
                  {L("💡 항목별 카테고리 편집 → 자동 분할")}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {cards.map(card => (
                  <ReceiptCard key={card.id} card={card}
                    txnCount={computeTxnCount(card)}
                    onRetry={() => retryCard(card.id)}
                    onRemove={() => removeCard(card.id)}
                    onToggleSelect={() => toggleSelect(card.id)}
                    onEdit={() => setEditorCard(card)}
                    onSetMode={(m) => setCardMode(card.id, m)}
                    onSetPartner={(p) => setCardPartner(card.id, p)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={footer}>
          <div style={{ fontSize: 11, color: '#666' }}>
            {running && <span>{L("⏳ OCR 진행 중...")}</span>}
            {!running && cards.length > 0 && (
              <span>{L("저장 시 거래")} <strong>{L("{0}건", stats.txnsToCreate)}</strong>{L("이 추가됩니다")}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} type="button"
              style={{ padding: '9px 16px', border: '1px solid #ddd', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
              {L("취소")}
            </button>
            <button onClick={saveAll} type="button"
              disabled={running || savingAll || stats.txnsToCreate === 0}
              style={{
                padding: '9px 20px', border: 'none',
                background: stats.txnsToCreate > 0 ? '#FF6F0F' : '#9ca3af',
                color: '#fff', borderRadius: 8,
                cursor: stats.txnsToCreate > 0 ? 'pointer' : 'not-allowed',
                fontSize: 13, fontWeight: 800, opacity: savingAll ? 0.6 : 1,
              }}>
              {savingAll ? L("저장 중...") : L("✓ {0}건 일괄 저장", stats.txnsToCreate)}
            </button>
          </div>
        </div>

        {editorCard && (
          <LineItemEditor
            card={editorCard}
            onSave={(newLineItems) => {
              updateCardLineItems(editorCard.id, newLineItems);
              setEditorCard(null);
            }}
            onClose={() => setEditorCard(null)}
          />
        )}
      </div>
    </div>
  );
}

// ── 업로드 드롭존 ────────────────────────────────────────────────────────────
function UploadDropzone({ cameraInputRef, galleryInputRef, onCameraClick, onFiles }) {
  return (
    <div style={{ padding: 20 }}>
      <div
        onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#FF6F0F'; e.currentTarget.style.background = '#FAFAFA'; }}
        onDragLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fff'; }}
        onDrop={e => {
          e.preventDefault();
          e.currentTarget.style.borderColor = '#d1d5db';
          e.currentTarget.style.background = '#fff';
          if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
        }}
        style={{ border: '2px dashed #d1d5db', borderRadius: 14, padding: '40px 24px', textAlign: 'center', background: '#fff', transition: 'all .15s' }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>📸</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#18181B', marginBottom: 6 }}>
          {L("영수증 입력")}
        </div>
        <div style={{ fontSize: 12, color: '#666', lineHeight: 1.6, marginBottom: 18 }}>
          {L("카메라로 1장씩 또는 갤러리에서 한 번에 여러 장 (최대 30장)")}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => onCameraClick ? onCameraClick() : cameraInputRef.current?.click()} type="button"
            style={{
              padding: '12px 22px', borderRadius: 10, border: 'none',
              background: '#FF6F0F', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
            {L("📷 카메라로 촬영")}
          </button>
          <button onClick={() => galleryInputRef.current?.click()} type="button"
            style={{
              padding: '12px 22px', borderRadius: 10, border: '1.5px solid #FF6F0F',
              background: '#fff', color: '#18181B', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
            {L("🖼 갤러리에서 선택")}
          </button>
        </div>
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
          style={{ display: 'none' }}
          onChange={e => { onFiles(e.target.files); e.target.value = ''; }} />
        <input ref={galleryInputRef} type="file" accept="image/*" multiple
          style={{ display: 'none' }}
          onChange={e => { onFiles(e.target.files); e.target.value = ''; }} />
      </div>
      <div style={{ marginTop: 18, padding: 14, background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af', lineHeight: 1.6 }}>
        <strong>{L("💡 한 영수증에 여러 카테고리가 섞여 있어도 OK")}</strong><br/>
        {L("AI가 영수증의 모든 품목을 추출하면, 카드의 \"항목 편집\"을 눌러 품목별 카테고리를 직접 선택할 수 있어요. 같은 카테고리는 자동 합산되고 다른 카테고리는 거래로 분할됩니다.")}
      </div>
    </div>
  );
}

// ── 영수증 카드 ──────────────────────────────────────────────────────────────
function ReceiptCard({ card, txnCount, onRetry, onRemove, onToggleSelect, onEdit, onSetMode, onSetPartner }) {
  const conf = card.ocr?.confidence || 0;
  const confColor = conf >= 0.85 ? '#FF6F0F' : conf >= 0.6 ? '#EA580C' : '#52525B';
  const confLabel = conf >= 0.85 ? L("높음") : conf >= 0.6 ? L("확인 필요") : L("낮음");
  const hasLineItems = card.lineItems && card.lineItems.length > 0;
  const willSplit = txnCount > 1;
  const isSimpleReceipt = card.ocr?.evidenceType === 'simple_receipt';
  const isStatement = card.ocr?.documentType === 'statement';
  const mode = card.mode || 'ledger';

  return (
    <div style={{
      background: '#fff', borderRadius: 12,
      border: card.selected && card.status === 'done' ? '2px solid #FF6F0F' : '1px solid #e5e7eb',
      overflow: 'hidden', transition: 'all .15s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ position: 'relative', height: 130, background: '#f3f4f6' }}>
        {card.previewUrl ? (
          <img src={card.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: card.status === 'done' ? 1 : 0.4 }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: 32 }}>📸</div>
        )}

        {card.status === 'queued' && <Overlay>{L("대기 중...")}</Overlay>}
        {card.status === 'processing' && (
          <Overlay>
            <div style={{ marginBottom: 6 }}>{L("AI 분석 중...")}</div>
            <div style={{ width: 80, height: 4, background: 'rgba(255,255,255,0.3)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ background: '#fff', height: '100%', width: `${card.progress}%`, transition: 'width .3s' }} />
            </div>
          </Overlay>
        )}
        {card.status === 'error' && <Overlay color="#52525B">⚠ {card.error || L("실패")}</Overlay>}

        {card.status === 'done' && (
          <div style={{ position: 'absolute', top: 8, left: 8, background: confColor, color: '#fff', padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 800 }}>
            {confLabel} {Math.round(conf * 100)}%
          </div>
        )}

        {card.status === 'done' && (
          <div onClick={onToggleSelect} style={{
            position: 'absolute', top: 8, right: 8, width: 24, height: 24, borderRadius: 6,
            background: card.selected ? '#FF6F0F' : 'rgba(255,255,255,0.9)',
            border: '2px solid', borderColor: card.selected ? '#FF6F0F' : '#999',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#fff', fontSize: 14, fontWeight: 800,
          }}>
            {card.selected ? '✓' : ''}
          </div>
        )}

        <button onClick={onRemove} type="button"
          style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer' }}>
          {L("제거")}
        </button>
      </div>

      <div style={{ padding: 12 }}>
        {card.status === 'error' ? (
          <button onClick={onRetry} type="button"
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #FF6F0F', background: '#fff', color: '#18181B', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {L("다시 시도")}
          </button>
        ) : card.status === 'done' && card.ocr ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <input
                type="text"
                value={card.ocr.partnerName || ''}
                onChange={(e) => onSetPartner && onSetPartner(e.target.value)}
                placeholder={L("(거래처 미인식 — 직접 입력)")}
                style={{
                  flex: 1, minWidth: 0,
                  fontSize: 13, fontWeight: 700, color: '#333',
                  border: card.ocr.partnerName ? '1px solid transparent' : '1px dashed #A1A1AA',
                  background: card.ocr.partnerName ? 'transparent' : '#FAFAFA',
                  borderRadius: 4, padding: '2px 6px',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.border = '1px solid #FF6F0F'}
                onBlur={(e) => e.target.style.border = card.ocr.partnerName ? '1px solid transparent' : '1px dashed #A1A1AA'}
              />
              {isStatement && (
                <span style={{
                  padding: '1px 6px', background: '#dbeafe',
                  color: '#1e40af', borderRadius: 4, fontSize: 10, fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}>{L("📋 거래명세서")}</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
              {card.ocr.date || L("(일자 미인식)")} {L("· {0}", M(card.ocr.total))}
              {isStatement && card.lineItems && (
                <span style={{ marginLeft: 6, color: '#1e40af' }}>{L("· {0}행", card.lineItems.length)}</span>
              )}
            </div>

            {hasLineItems ? (
              <div style={{ background: willSplit ? '#F4F4F5' : '#FAFAFA', borderRadius: 6, padding: '6px 8px', fontSize: 11, color: willSplit ? '#52525B' : '#EA580C', marginBottom: 8 }}>
                <div style={{ fontWeight: 700 }}>
                  {L("📂 품목 {0}개 → 거래 {1}건", card.lineItems.filter(li => li.included !== false).length, txnCount)}{willSplit ? L(" (분할)") : ''}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
                📁 {categoryLabel(card.ocr.category?.major, card.ocr.category?.sub)}
              </div>
            )}

            {/* 모드 토글: 거래장부 vs 메모 (간이영수증 안내) */}
            <div style={{
              display: 'flex', gap: 3, marginBottom: 6,
              background: '#f3f4f6', borderRadius: 6, padding: 2,
            }}>
              <button type="button" onClick={() => onSetMode && onSetMode('ledger')} style={{
                flex: 1, padding: '4px 6px', border: 'none', borderRadius: 4,
                background: mode === 'ledger' ? '#FF6F0F' : 'transparent',
                color: mode === 'ledger' ? '#fff' : '#6b7280',
                fontSize: 10, fontWeight: 700, cursor: 'pointer',
              }}>{L("📒 거래장부")}</button>
              <button type="button" onClick={() => onSetMode && onSetMode('memo')} style={{
                flex: 1, padding: '4px 6px', border: 'none', borderRadius: 4,
                background: mode === 'memo' ? '#EA580C' : 'transparent',
                color: mode === 'memo' ? '#fff' : '#6b7280',
                fontSize: 10, fontWeight: 700, cursor: 'pointer',
              }}>{L("📋 메모만")}</button>
            </div>
            {mode === 'memo' && (
              <div style={{
                fontSize: 10, color: '#78350f', background: '#FAFAFA',
                padding: '4px 7px', borderRadius: 4, marginBottom: 6, lineHeight: 1.4,
              }}>
                💡 {isStatement ? L("거래장부 안 들어감. 품목·단가는 자산화 (단가표·재고 자동 반영). 부가세 신고는 발행받은 세금계산서로 별도 처리.") : L("거래장부 안 들어감. 품목·단가만 자산화. 정산 시 세금계산서·카드매출전표로 별도 입력 필요")}
              </div>
            )}
            {mode === 'ledger' && isSimpleReceipt && (
              <div style={{
                fontSize: 10, color: '#52525B', background: '#F4F4F5',
                padding: '4px 7px', borderRadius: 4, marginBottom: 6, lineHeight: 1.4,
              }}>
                {L("⚠ 간이영수증 — 매입세액 공제 X. \"메모만\" 모드 권장")}
              </div>
            )}
            <button onClick={onEdit} type="button"
              style={{ width: '100%', padding: '7px 8px', borderRadius: 6, border: '1px solid #FF6F0F', background: '#fff', color: '#18181B', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {hasLineItems ? L("✏️ 항목 편집") : L("✏️ 직접 편집")}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Overlay({ children, color }) {
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: color || '#fff', fontSize: 13, fontWeight: 700, textAlign: 'center', padding: 8 }}>
      {children}
    </div>
  );
}

// ── LineItemEditor — 영수증 모든 품목을 한 번에 펼쳐서 카테고리 선택 ─────────
function LineItemEditor({ card, onSave, onClose }) {
  // lineItems 없으면 빈 배열로 시작 (사용자가 + 추가 가능)
  const initial = (card.lineItems && card.lineItems.length > 0)
    ? card.lineItems
    : [{
        id: 'li_0',
        itemName: card.ocr?.items?.[0] || '',
        amount: card.ocr?.total || 0,
        category: card.ocr?.category || { major: 'material', sub: 'raw_material' },
        presumedCandidate: !!card.ocr?.presumedCandidate,
        presumedReason: card.ocr?.presumedReason || null,
        included: true,
      }];

  const [items, setItems] = useState(initial);

  const update = (idx, patch) => setItems(prev => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const remove = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const add = () => setItems(prev => [...prev, {
    id: `li_${Date.now()}`,
    itemName: '',
    amount: 0,
    category: { major: 'material', sub: 'raw_material' },
    presumedCandidate: false,
    presumedReason: null,
    included: true,
  }]);

  // 카테고리별 그룹핑 미리보기 — 음수 amount(반품/할인)도 포함
  const includedItems = items.filter(li => li.included !== false && (Number(li.amount) || 0) !== 0);
  const groupMap = new Map();
  for (const li of includedItems) {
    const key = `${li.category?.major || 'etc'}::${li.category?.sub || ''}`;
    if (!groupMap.has(key)) {
      groupMap.set(key, { category: li.category, amount: 0, names: [] });
    }
    const g = groupMap.get(key);
    g.amount += Number(li.amount) || 0;
    if (li.itemName) g.names.push(li.itemName);
  }
  const groups = Array.from(groupMap.values());

  const sumAmount = includedItems.reduce((s, x) => s + (Number(x.amount) || 0), 0);

  // 부가세 분해 미리보기 (양수 매입 라인만, 면세/과세 분리)
  let supplyTotal = 0, vatTotal = 0, taxFreeTotal = 0, presumedTotal = 0;
  for (const li of includedItems) {
    const amt = Number(li.amount) || 0;
    if (amt <= 0) continue; // 반품/할인은 별도
    if (li.isTaxFree) {
      taxFreeTotal += amt;
      if (li.presumedCandidate) presumedTotal += amt;
    } else {
      const supply = Math.round(amt / 1.1);
      supplyTotal += supply;
      vatTotal += amt - supply;
    }
  }

  const expectedTotal = card.ocr?.total || 0;
  const diff = sumAmount - expectedTotal;
  const diffPct = expectedTotal > 0 ? Math.abs(diff / expectedTotal) * 100 : 0;
  const matchStatus = expectedTotal === 0 ? 'ok' : diffPct < 5 ? 'ok' : diffPct < 15 ? 'warn' : 'mismatch';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 760, maxWidth: '100%', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #eee', background: '#f9fafb' }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>{L("📂 품목별 카테고리 편집")}</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>
            {card.ocr?.partnerName || L("(거래처)")} {L("· 합계 {0}", M(expectedTotal))}
          </div>
          <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
            {L("품목별로 카테고리를 선택하세요. 같은 카테고리는 자동 합산되고, 다른 카테고리는 거래로 분할됩니다.")}
          </div>
        </div>

        <div style={{ padding: 16, overflow: 'auto', flex: 1, background: '#fafafa' }}>
          {/* 품목 리스트 */}
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>
              {L("인식된 품목이 없습니다. 아래 \"+ 항목 추가\"로 직접 입력해주세요.")}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {items.map((li, idx) => (
                <LineItemRow key={li.id || idx} item={li} idx={idx}
                  evidenceType={card.ocr?.evidenceType}
                  onUpdate={(patch) => update(idx, patch)}
                  onRemove={() => remove(idx)} />
              ))}
            </div>
          )}

          <button onClick={add} type="button"
            style={{ width: '100%', padding: 9, borderRadius: 8, border: '1.5px dashed #FF6F0F', background: '#fff', color: '#18181B', fontWeight: 700, fontSize: 12, cursor: 'pointer', marginBottom: 14 }}>
            {L("+ 항목 추가 (영수증에서 누락된 게 있을 때)")}
          </button>

          {/* 카테고리별 그룹 미리보기 */}
          {groups.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 10, padding: 12, border: '1px solid #e5e7eb', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 8 }}>
                {L("💾 저장 시 거래 {0}건으로 추가됩니다", groups.length)}
              </div>
              {groups.map((g, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < groups.length - 1 ? '1px dashed #f0f0f0' : 'none' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#18181B' }}>
                      {categoryLabel(g.category?.major, g.category?.sub)}
                    </div>
                    <div style={{ fontSize: 10, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {g.names.slice(0, 4).join(', ')}{g.names.length > 4 ? L(" 외 {0}건", g.names.length - 4) : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                    {M(g.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 합계 검증 */}
          <div style={{
            padding: 12, borderRadius: 8,
            background: matchStatus === 'ok' ? '#FAFAFA' : matchStatus === 'warn' ? '#FAFAFA' : '#fef2f2',
            border: '1px solid',
            borderColor: matchStatus === 'ok' ? '#FED7AA' : matchStatus === 'warn' ? '#D4D4D8' : '#fca5a5',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span>{L("포함 항목 합계")}</span>
              <strong>{M(sumAmount)}</strong>
            </div>

            {/* 부가세 분해 미리보기 — 저장 시 거래장부에 이대로 들어감 */}
            {(supplyTotal > 0 || taxFreeTotal > 0) && (
              <div style={{ marginBottom: 6, padding: '6px 10px', background: 'rgba(255,255,255,0.6)', borderRadius: 5, fontSize: 11, color: '#475569' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{L("↳ 공급가액 (과세)")}</span>
                  <strong>{M(supplyTotal)}</strong>
                </div>
                {vatTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#18181B' }}>
                    <span>{L("↳ 부가세 (매입세액 공제)")}</span>
                    <strong>{M(vatTotal)}</strong>
                  </div>
                )}
                {taxFreeTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#854d0e' }}>
                    <span>{L("↳ 면세 공급가액")}</span>
                    <strong>{M(taxFreeTotal)}</strong>
                  </div>
                )}
                {presumedTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#9A3412', marginTop: 2, paddingTop: 2, borderTop: '1px dashed #d1d5db' }}>
                    <span>{L("🎁 의제매입공제 후보 합계")}</span>
                    <strong>{L("{0} → 공제 약 {1}", M(presumedTotal), M(Math.round(presumedTotal * 8 / 108)))}</strong>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span>{L("영수증 총액")}</span>
              <strong>{M(expectedTotal)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700,
              color: matchStatus === 'ok' ? '#EA580C' : matchStatus === 'warn' ? '#52525B' : '#52525B' }}>
              <span>{L("차이")}</span>
              <span>{diff >= 0 ? '+' : ''}{L("{0} ({1}%)", M(diff), diffPct.toFixed(1))}</span>
            </div>

            {/* 자동 보정 버튼 — 차이가 ±1% 초과면 표시 */}
            {expectedTotal > 0 && diffPct >= 1 && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {diff > 0 && (
                  <>
                    <button type="button"
                      onClick={() => {
                        // 차이만큼 음수 amount의 할인 lineItem 추가
                        const cat = items.find(x => x.included !== false && (Number(x.amount) || 0) > 0)?.category
                                 || { major: 'material', sub: 'raw_material' };
                        setItems(prev => [...prev, {
                          id: `li_disc_${Date.now()}`,
                          itemName: '할인·적립 (자동 보정)',
                          amount: -diff,
                          isDiscount: true,
                          category: cat,
                          presumedCandidate: false,
                          included: true,
                        }]);
                      }}
                      style={{
                        padding: '8px 12px', borderRadius: 7, border: '1px solid #FF6F0F',
                        background: '#FF6F0F', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      }}>
                      {L("➕ \"할인 −{0}\" 항목 자동 추가 (영수증 총액에 맞춤)", M(diff))}
                    </button>
                    <button type="button"
                      onClick={() => {
                        // 모든 항목 amount를 비례 차감 → 영수증 총액 정확히 일치
                        const ratio = expectedTotal / sumAmount;
                        setItems(prev => prev.map(li => {
                          if (li.included === false) return li;
                          const a = Number(li.amount) || 0;
                          if (a === 0) return li;
                          return { ...li, amount: Math.round(a * ratio) };
                        }));
                      }}
                      style={{
                        padding: '7px 12px', borderRadius: 7, border: '1px solid #ddd',
                        background: '#fff', color: '#555', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      }}>
                      {L("또는: 모든 항목 금액을 {0}%로 비례 차감", ((expectedTotal/sumAmount)*100).toFixed(1))}
                    </button>
                  </>
                )}
                {diff < 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 11, color: '#52525B', lineHeight: 1.5 }}>
                      {L("⚠ 항목 합계가 영수증 총액보다 작습니다.")}<br/>
                      {L("• 누락된 품목이 있다면 \"+ 항목 추가\"로 채워주세요")}<br/>
                      {L("• 또는 OCR이 영수증 총액을 잘못 인식했을 수 있어요 (외상잔액 등을 합계로 오인)")}
                    </div>
                    {/* 부가세 누락 자동 감지 — 차이가 영수증 vat 근처면 */}
                    {(() => {
                      const ocrVat = Number(card.ocr?.vat) || 0;
                      const absDiff = Math.abs(diff);
                      const vatLike = ocrVat > 0 && Math.abs(absDiff - ocrVat) <= ocrVat * 0.1;
                      if (!vatLike) return null;
                      return (
                        <button type="button"
                          onClick={() => {
                            // 가장 큰 양수 amount lineItem에 vat 더하기 (단일 라인이면 거기에)
                            const ratio = (sumAmount + ocrVat) / sumAmount;
                            setItems(prev => prev.map(li => {
                              if (li.included === false) return li;
                              const a = Number(li.amount) || 0;
                              if (a <= 0) return li;  // 음수(반품/할인)는 그대로
                              return { ...li, amount: Math.round(a * ratio) };
                            }));
                          }}
                          style={{
                            padding: '8px 12px', borderRadius: 7,
                            border: '1px solid #FF6F0F', background: '#FF6F0F', color: '#fff',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          }}>
                          {L("✅ 부가세 {0} 포함해서 항목 보정 (차이 = 부가세로 추정)", M(ocrVat))}
                        </button>
                      );
                    })()}
                    <div style={{ fontSize: 11, color: '#0369a1', background: '#f0f9ff', padding: '6px 10px', borderRadius: 6, lineHeight: 1.5 }}>
                      {L("💾 저장 시에는")} <strong>{L("항목 합계 {0}", M(sumAmount))}</strong>{L("으로 거래장부에 들어갑니다 (영수증 총액은 참고용)")}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #eee', display: 'flex', gap: 8, justifyContent: 'flex-end', background: '#fff' }}>
          <button onClick={onClose} type="button"
            style={{ padding: '8px 16px', border: '1px solid #ddd', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
            {L("취소")}
          </button>
          <button onClick={() => onSave(items)} type="button"
            disabled={items.length === 0}
            style={{
              padding: '8px 18px', border: 'none',
              background: items.length > 0 ? '#FF6F0F' : '#9ca3af',
              color: '#fff', borderRadius: 8,
              cursor: items.length > 0 ? 'pointer' : 'not-allowed',
              fontSize: 13, fontWeight: 700,
            }}>
            {L("저장")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 품목 1행 ────────────────────────────────────────────────────────────────
const QUALIFIED_EVIDENCE = new Set(['card', 'cash_receipt', 'tax_invoice']);
function LineItemRow({ item, idx, evidenceType, onUpdate, onRemove }) {
  const setAmount = (v) => {
    const raw = String(v).replace(/[^\d-]/g, '');
    const n = Number(raw) || 0;
    onUpdate({ amount: n });
  };

  // 라인 종류 변경 — 부호 자동 처리
  const setKind = (kind) => {
    const absAmount = Math.abs(Number(item.amount) || 0);
    if (kind === 'purchase') {
      onUpdate({ isReturn: false, isDiscount: false, amount: absAmount });
    } else if (kind === 'return') {
      onUpdate({ isReturn: true, isDiscount: false, amount: -absAmount });
    } else if (kind === 'discount') {
      onUpdate({ isReturn: false, isDiscount: true, amount: -absAmount });
    }
  };

  const dimmed = item.included === false;
  const isReturn = !!item.isReturn;
  const isDiscount = !isReturn && !!item.isDiscount;
  const isTaxFree = !!item.isTaxFree;
  const currentKind = isReturn ? 'return' : isDiscount ? 'discount' : 'purchase';
  const hasInlineDiscount = (Number(item.discountAmount) || 0) > 0;
  const originalAmount = hasInlineDiscount ? (Number(item.amount) || 0) + Number(item.discountAmount) : null;

  // 반품 = 보라, 할인 = 빨강, 정상 = 초록
  const borderColor = dimmed ? '#e5e7eb' : isReturn ? '#c4b5fd' : isDiscount ? '#fca5a5' : '#FAFAFA';
  const bgColor = dimmed ? '#fff' : isReturn ? '#faf5ff' : isDiscount ? '#fff5f5' : '#fff';
  const negColor = isReturn ? '#7c3aed' : '#52525B';

  return (
    <div style={{
      background: bgColor, borderRadius: 10, padding: '10px 12px',
      border: '1px solid', borderColor,
      opacity: dimmed ? 0.5 : 1, transition: 'all .15s',
    }}>
      {/* 거래명세서 행별 거래일자 (있을 때만) */}
      {item.itemDate && (
        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>
          📅 {item.itemDate}
        </div>
      )}

      {/* 첫 줄: 체크박스 + 품목명 (가용 폭 전체) + 삭제 */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <input type="checkbox"
          checked={item.included !== false}
          onChange={e => onUpdate({ included: e.target.checked })}
          style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
        <input type="text" placeholder={L("품목명")}
          value={item.itemName || ''}
          onChange={e => onUpdate({ itemName: e.target.value })}
          style={{ flex: 1, minWidth: 0, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
        <button onClick={onRemove} type="button"
          aria-label={L("이 항목 삭제")}
          style={{ background: 'none', border: 'none', color: '#52525B', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>
          ✕
        </button>
      </div>

      {/* 라인 종류 셀렉터 (사용자가 직접 변경 가능) */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6, background: '#f3f4f6', padding: 3, borderRadius: 7 }}>
        {[
          { id: 'purchase', label: L("🟢 매입"),    color: '#18181B', bg: '#FAFAFA' },
          { id: 'return',   label: L("🟣 반품"),    color: '#7c3aed', bg: '#ede9fe' },
          { id: 'discount', label: L("🔴 할인"),    color: '#52525B', bg: '#F4F4F5' },
        ].map(k => (
          <button key={k.id} type="button"
            onClick={() => setKind(k.id)}
            style={{
              flex: 1, padding: '4px 6px',
              border: 'none',
              background: currentKind === k.id ? k.color : 'transparent',
              color: currentKind === k.id ? '#fff' : '#6b7280',
              fontSize: 11, fontWeight: 700,
              borderRadius: 5, cursor: 'pointer',
            }}>
            {Lv(k.label)}
          </button>
        ))}
      </div>

      {/* 면세/과세 토글 (부가세 신고 정확성) */}
      <label style={{
        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
        padding: '5px 10px', borderRadius: 6,
        background: isTaxFree ? '#fefce8' : '#f3f4f6',
        border: '1px solid', borderColor: isTaxFree ? '#fde047' : '#e5e7eb',
        cursor: 'pointer',
      }}>
        <input type="checkbox" checked={isTaxFree}
          onChange={e => onUpdate({ isTaxFree: e.target.checked, presumedCandidate: e.target.checked ? !!item.presumedCandidate : false })}
          style={{ width: 14, height: 14, cursor: 'pointer' }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: isTaxFree ? '#854d0e' : '#6b7280' }}>
          {L("🚫 면세 (부가세 X) — 농수축산물·면세상품")}
        </span>
        {isTaxFree && (
          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#854d0e', fontStyle: 'italic' }}>
            {L("매입세액 공제 X")}
          </span>
        )}
      </label>

      {/* 의제매입공제 후보 토글 — 면세 + 적격증빙일 때만 (V16-A/B) */}
      {isTaxFree && (QUALIFIED_EVIDENCE.has(evidenceType) ? (
        <label style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
          padding: '5px 10px', borderRadius: 6,
          background: item.presumedCandidate ? '#FAFAFA' : '#f9fafb',
          border: '1px solid', borderColor: item.presumedCandidate ? '#FED7AA' : '#e5e7eb',
          cursor: 'pointer',
        }}>
          <input type="checkbox" checked={!!item.presumedCandidate}
            onChange={e => onUpdate({ presumedCandidate: e.target.checked })}
            style={{ width: 14, height: 14, cursor: 'pointer' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: item.presumedCandidate ? '#9A3412' : '#6b7280' }}>
            {L("🎁 의제매입공제 후보 — 1차 농수축산물")}
          </span>
          {item.presumedCandidate && (
            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#9A3412', fontStyle: 'italic' }}>
              {L("부가세 신고 시 공제")}
            </span>
          )}
        </label>
      ) : (
        <div style={{
          fontSize: 10, color: '#52525B', background: '#F4F4F5',
          padding: '4px 7px', borderRadius: 4, marginBottom: 6, lineHeight: 1.4,
        }}>
          {L("⚠ 의제매입공제 불가 — 적격증빙(카드/현금영수증/세금계산서)만 가능")}
        </div>
      ))}

      {/* 둘째 줄: 금액 (단독 라인, 풀 폭) */}
      <div style={{ position: 'relative', marginBottom: hasInlineDiscount || isDiscount || isReturn ? 4 : 6 }}>
        <input type="text" inputMode="numeric" placeholder={L("금액")}
          value={item.amount !== 0 && item.amount !== null && item.amount !== undefined ? fmt(item.amount) : ''}
          onChange={e => setAmount(e.target.value)}
          style={{
            width: '100%', padding: '7px 26px 7px 10px',
            border: '1px solid', borderColor: isReturn ? '#c4b5fd' : isDiscount ? '#fca5a5' : '#ddd',
            borderRadius: 6, fontSize: 13, textAlign: 'right', boxSizing: 'border-box',
            color: (isReturn || isDiscount) ? negColor : '#1a1a1a',
            fontWeight: (isReturn || isDiscount) ? 700 : 400,
          }} />
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: (isReturn || isDiscount) ? negColor : '#888', pointerEvents: 'none' }}>{MU()}</span>
      </div>

      {/* 수량 × 단가 표시 (있으면) */}
      {(item.quantity && item.quantity !== 1 && item.unitPrice > 0) && (
        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6, textAlign: 'right' }}>
          {Math.abs(item.quantity)}{item.quantity < 0 ? L("개 (반품)") : L("개")} {L("× {0}", M(item.unitPrice))}
        </div>
      )}

      {/* 공급가액 / 부가세 분해 표시 — 사용자 신뢰 (자동 분리됨을 시각화) */}
      {Number(item.amount) > 0 && !isReturn && !isDiscount && (
        isTaxFree ? (
          <div style={{ fontSize: 10, color: '#854d0e', marginBottom: 6, textAlign: 'right',
                        background: '#fefce8', padding: '4px 8px', borderRadius: 4,
                        border: '1px dashed #fde047' }}>
            {L("🚫 면세 공급가액")} <strong>{M(item.amount)}</strong> {L("· 부가세 0원")}
          </div>
        ) : (
          <div style={{ fontSize: 10, color: '#0369a1', marginBottom: 6, textAlign: 'right',
                        background: '#eff6ff', padding: '4px 8px', borderRadius: 4,
                        border: '1px dashed #93c5fd' }}>
            {L("공급가액")} <strong>{M(Math.round(Number(item.amount) / 1.1))}</strong>
            {' + '}
            {L("부가세")} <strong style={{ color: '#18181B' }}>{M(Number(item.amount) - Math.round(Number(item.amount) / 1.1))}</strong>
            {' = '}{M(item.amount)}
          </div>
        )
      )}

      {/* Case A: 항목 종속 할인 — 정가/할인 작게 표시 */}
      {hasInlineDiscount && (
        <div style={{ fontSize: 10, color: '#52525B', marginBottom: 6, textAlign: 'right' }}>
          {L("정가 {0} − 할인 {1}", M(originalAmount), M(item.discountAmount))}
        </div>
      )}
      {isReturn && (
        <div style={{ fontSize: 10, color: '#7c3aed', marginBottom: 6, fontWeight: 700 }}>
          {L("↩ 반품/교환 라인 (음수 금액으로 처리 — 영수증 합계는 차액)")}
        </div>
      )}
      {isDiscount && !hasInlineDiscount && !isReturn && (
        <div style={{ fontSize: 10, color: '#52525B', marginBottom: 6, fontWeight: 600 }}>
          {L("🏷 할인·적립 라인")}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        <select value={item.category?.major || 'etc'}
          onChange={e => onUpdate({ category: { major: e.target.value, sub: null } })}
          style={{ flex: 1, minWidth: 0, padding: '7px 7px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, background: '#fff' }}>
          {CATEGORY_MAJOR.map(m => (
            <option key={m.id} value={m.id}>{m.emoji} {Lv(m.label)}</option>
          ))}
        </select>
        <select value={item.category?.sub || ''}
          onChange={e => onUpdate({ category: { ...item.category, sub: e.target.value || null } })}
          style={{ flex: 1, minWidth: 0, padding: '7px 7px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, background: '#fff' }}>
          <option value="">{L("(소분류)")}</option>
          {(CATEGORY_SUB[item.category?.major] || []).map(s => (
            <option key={s.id} value={s.id}>{Lv(s.label)}</option>
          ))}
        </select>
      </div>

    </div>
  );
}


// ── 💻 PC 웹캠 모달 (V23) ─────────────────────────────────────────────
function WebcamModal({ onCapture, onClose }) {
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const [stream, setStream] = React.useState(null);
  const [error, setError] = React.useState('');
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    let s = null;
    (async () => {
      try {
        s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (!active) { s.getTracks().forEach(t => t.stop()); return; }
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (e) {
        setError(e.message || L("카메라 접근 실패"));
      }
    })();
    return () => {
      active = false;
      if (s) s.getTracks().forEach(t => t.stop());
    };
  }, []);

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'receipt_' + Date.now() + '.jpg', { type: 'image/jpeg' });
      if (stream) stream.getTracks().forEach(t => t.stop());
      onCapture(file);
    }, 'image/jpeg', 0.92);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#000', borderRadius: 14, maxWidth: 880, width: '100%', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', background: '#111' }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>{L("📷 영수증 촬영")}</span>
          <button onClick={onClose} type="button"
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: 4 }}>
            ×
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', minHeight: 300 }}>
          {error ? (
            <div style={{ color: '#fca5a5', fontSize: 14, padding: 40, textAlign: 'center', lineHeight: 1.6 }}>
              {L("⚠ 카메라 접근 실패")}<br />
              <span style={{ fontSize: 12 }}>{error}</span><br />
              <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 8, display: 'block' }}>
                {L("브라우저 주소창 왼쪽 자물쇠 → 카메라 권한 허용 확인")}
              </span>
            </div>
          ) : (
            <video ref={videoRef} autoPlay playsInline muted
              style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block' }} />
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
        <div style={{ padding: 14, display: 'flex', justifyContent: 'center', gap: 12, background: '#111' }}>
          <button onClick={onClose} type="button"
            style={{ padding: '10px 22px', borderRadius: 8, border: '1px solid #374151', background: 'transparent', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {L("취소")}
          </button>
          <button onClick={capture} disabled={!ready || !!error} type="button"
            style={{ padding: '10px 32px', borderRadius: 8, border: 'none',
              background: (ready && !error) ? '#FF6F0F' : '#374151',
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: (ready && !error) ? 'pointer' : 'not-allowed' }}>
            {L("📸 촬영")}
          </button>
        </div>
      </div>
    </div>
  );
}
